import React, { useState, useMemo, useContext } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Button,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import axios from 'axios';

import CustomOrderArrow from './IconosOrdenacion';
import { TipusMovIcon } from './IconMap';
import { SelectConcepteTextField } from './SelectConcepte';
import ConcepteMovementDisplay from './ConcepteMovementDisplay';
import AppContext from '../AppContext';
import config from '../../config';
import { formatEuro } from '../../functions/formatNumber';
import { strDateToShort, strDateToDDMMYYYY } from '../../functions/formatDate';
import { getColorForAmount } from '../../functions/colors';
import { buildFullTreeWithEntities, resolveMovementConceptAndPath } from '../../services/concepteGraphService';

/**
 * MovimentsPendentsValidarList
 * Mostra i gestiona la llista de conceptes / articles en estat PENDENT-VALIDAR.
 * Cada element de la llista és un ConcepteMoviment amb el seu moviment bancari pare associat.
 */
const MovimentsPendentsValidarList = ({
  moviments = [],
  showYear = false,
  listConceptes,
  onError,
  onEditComplete,
  userapp
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'data', direction: 'desc' });
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [loadingActionId, setLoadingActionId] = useState(null);
  const [confirmValidateAllOpen, setConfirmValidateAllOpen] = useState(false);
  const [isValidatingAll, setIsValidatingAll] = useState(false);

  const { ctxUser, conceptesTree, entitatsTransversals, conceptesNodesMap, conceptesList } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
  const targetConceptList = listConceptes || conceptesList || [];

  const handleSort = (event, key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    event.stopPropagation();
  };

  const sortedMoviments = useMemo(() => {
    if (!Array.isArray(moviments)) return [];
    return [...moviments].sort((a, b) => {
      switch (sortConfig.key) {
        case 'moviment': {
          const mA = a.moviment?.moviment || a.moviment || '';
          const mB = b.moviment?.moviment || b.moviment || '';
          return sortConfig.direction === 'asc'
            ? mA.localeCompare(mB)
            : mB.localeCompare(mA);
        }
        case 'import':
          return sortConfig.direction === 'asc'
            ? (a.importe || 0) - (b.importe || 0)
            : (b.importe || 0) - (a.importe || 0);
        case 'data':
          return sortConfig.direction === 'asc'
            ? new Date(a.data) - new Date(b.data)
            : new Date(b.data) - new Date(a.data);
        case 'tipus': {
          const tA = a.moviment?.tipus || '';
          const tB = b.moviment?.tipus || '';
          return sortConfig.direction === 'asc'
            ? tA.localeCompare(tB)
            : tB.localeCompare(tA);
        }
        case 'concepte': {
          const cA = a.conceptePath || a.concepte || '';
          const cB = b.conceptePath || b.concepte || '';
          return sortConfig.direction === 'asc'
            ? cA.localeCompare(cB)
            : cB.localeCompare(cA);
        }
        default:
          return 0;
      }
    });
  }, [moviments, sortConfig]);

  const enrichedTree = useMemo(() => {
    return buildFullTreeWithEntities(conceptesTree, entitatsTransversals);
  }, [conceptesTree, entitatsTransversals]);

  // Validar concepte/article individual (aprovar tal com està proposat per Amazon)
  const handleValidateSingle = async (concepteMovimentId) => {
    try {
      setLoadingActionId(concepteMovimentId);
      const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};
      await axios.put(
        `${config.apiBaseUrl}/v1/concmovs/validar/${concepteMovimentId}`,
        {},
        { headers }
      );
      if (onEditComplete) onEditComplete();
    } catch (err) {
      console.error('Error al validar concepte:', err);
      if (onError) onError(err, 'validar concepte');
    } finally {
      setLoadingActionId(null);
    }
  };

  // Iniciar edició de concepte i descripció
  const handleStartEdit = (cm) => {
    setEditingId(cm.id);
    setEditingData({ ...cm });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const onChangeConcepte = (e, selectedNode) => {
    if (e.stopPropagation) e.stopPropagation();
    const node = selectedNode || e.target?.node;
    if (node) {
      const isN3 = Boolean(node.nodeN3);
      setEditingData(prev => ({
        ...prev,
        concepte: null, // El concepte clàssic el resol oficialment el backend
        nodeFulla: node,
        id_node_fulla: node.id,
        idNodeFulla: node.id,
        conceptePath: node.conceptePath,
        nodeN1: node.nodeN1 || null,
        nodeN2: node.nodeN2 || null,
        nodeN3: isN3 ? (node.nodeN3 || null) : null,
        id_node_n1: node.nodeN1?.id || null,
        id_node_n2: node.nodeN2?.id || null,
        id_node_n3: isN3 ? (node.nodeN3?.id || null) : null,
        idNodeN1: node.nodeN1?.id || null,
        idNodeN2: node.nodeN2?.id || null,
        idNodeN3: isN3 ? (node.nodeN3?.id || null) : null,
      }));
    } else {
      setEditingData(prev => ({
        ...prev,
        concepte: e.target?.value || '',
        nodeFulla: null,
        id_node_fulla: null,
        idNodeFulla: null,
        conceptePath: null,
        nodeN1: null,
        nodeN2: null,
        nodeN3: null,
        id_node_n1: null,
        id_node_n2: null,
        id_node_n3: null,
        idNodeN1: null,
        idNodeN2: null,
        idNodeN3: null,
      }));
    }
  };

  // Guardar concepte modificat i validar (unificat amb MovimentItemEdit)
  const handleSaveAndValidate = async (concepteMovimentId) => {
    try {
      setLoadingActionId(concepteMovimentId);
      const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};

      const toNodeRef = (val) => {
        if (!val) return null;
        const id = typeof val === 'object' ? val.id : val;
        return id ? { id: Number(id) } : null;
      };

      const isHierarchyNode = Boolean(
        editingData?.nodeFulla || editingData?.idNodeFulla || editingData?.id_node_fulla || editingData?.nodeN1 || editingData?.idNodeN1
      );

      const payload = {
        ...editingData,
        estat: 'OK',
        concepte: isHierarchyNode ? null : (editingData?.concepte || null),
        nodeN1: toNodeRef(editingData?.nodeN1 || editingData?.idNodeN1 || editingData?.id_node_n1),
        nodeN2: toNodeRef(editingData?.nodeN2 || editingData?.idNodeN2 || editingData?.id_node_n2),
        nodeN3: toNodeRef(editingData?.nodeN3 || editingData?.idNodeN3 || editingData?.id_node_n3),
        nodeFulla: toNodeRef(editingData?.nodeFulla || editingData?.idNodeFulla || editingData?.id_node_fulla),
      };

      try {
        await axios.put(
          `${config.apiBaseUrl}/v1/concmovs/${concepteMovimentId}`,
          payload,
          { headers }
        );
      } catch (putErr) {
        // Fallback defensiu a /validar si l'endpoint estàndard fallés
        console.warn('Fallback a /v1/concmovs/validar:', putErr);
        await axios.put(
          `${config.apiBaseUrl}/v1/concmovs/validar/${concepteMovimentId}`,
          payload,
          { headers }
        );
      }
      setEditingId(null);
      setEditingData(null);
      if (onEditComplete) onEditComplete();
    } catch (err) {
      console.error('Error al modificar i validar concepte:', err);
      if (onError) onError(err, 'modificar concepte');
    } finally {
      setLoadingActionId(null);
    }
  };

  // Validar tots els conceptes pendents de validar
  const handleValidateAll = async () => {
    if (!userapp) return;
    try {
      setIsValidatingAll(true);
      const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};

      try {
        // Intentar primer l'endpoint massiu del backend
        await axios.put(
          `${config.apiBaseUrl}/v1/concmovs/validarmoviments/${userapp}`,
          {},
          { headers }
        );
      } catch (massError) {
        // Fallback: si l'endpoint massiu no estigués disponible, validar cadascun individualment
        console.warn('Endpoint massiu no disponible, executant validació individual per lot:', massError);
        const promises = sortedMoviments.map(cm =>
          axios.put(`${config.apiBaseUrl}/v1/concmovs/validar/${cm.id}`, {}, { headers })
        );
        await Promise.all(promises);
      }

      setConfirmValidateAllOpen(false);
      if (onEditComplete) onEditComplete();
    } catch (err) {
      console.error('Error al validar tots els conceptes:', err);
      if (onError) onError(err, 'validar tots els conceptes');
    } finally {
      setIsValidatingAll(false);
    }
  };

  const totalMoviments = moviments.length;

  return (
    <Paper sx={{ padding: '20px' }}>
      {/* Barra superior d'acció global */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          pb: 1.5,
          borderBottom: '1px solid #e0e0e0',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingBagIcon sx={{ color: '#ff9900' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Articles i propostes d'Amazon pendents de validar ({totalMoviments})
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={isValidatingAll ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
          onClick={() => setConfirmValidateAllOpen(true)}
          disabled={totalMoviments === 0 || isValidatingAll}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          Validar Tots ({totalMoviments})
        </Button>
      </Box>

      {/* Capçalera del Grid / Taula */}
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{
          marginBottom: 2,
          backgroundColor: '#f0f0f0',
          color: '#000',
          paddingTop: '4px',
          paddingBottom: '4px',
          paddingLeft: '10px',
          paddingRight: '10px',
          borderBottom: '2px solid #ddd'
        }}
      >
        <Grid size={{ xs: 12, sm: 1 }} onClick={(e) => handleSort(e, 'data')} sx={{ cursor: 'pointer' }}>
          <Typography variant="small">
            Data {sortConfig.key === 'data' && <CustomOrderArrow direction={sortConfig.direction} />}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 0.5 }} onClick={(e) => handleSort(e, 'tipus')} sx={{ cursor: 'pointer' }}>
          <Typography variant="small">
            Tipus {sortConfig.key === 'tipus' && <CustomOrderArrow direction={sortConfig.direction} />}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 2.5 }} onClick={(e) => handleSort(e, 'concepte')} sx={{ cursor: 'pointer' }}>
          <Typography variant="small">
            Concepte Proposat {sortConfig.key === 'concepte' && <CustomOrderArrow direction={sortConfig.direction} />}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 3.5 }} onClick={(e) => handleSort(e, 'moviment')} sx={{ cursor: 'pointer' }}>
          <Typography variant="small">
            Moviment {sortConfig.key === 'moviment' && <CustomOrderArrow direction={sortConfig.direction} />}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 2 }}>
          <Typography variant="small">
            Descripció / Article Amazon
          </Typography>
        </Grid>

        <Grid
          size={{ xs: 12, sm: 1.2 }}
          container
          direction="column"
          alignItems="flex-end"
          onClick={(e) => handleSort(e, 'import')}
          sx={{ cursor: 'pointer' }}
        >
          <Typography variant="small">
            Import {sortConfig.key === 'import' && <CustomOrderArrow direction={sortConfig.direction} />}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 1.3 }} sx={{ textAlign: 'right' }}>
          <Typography variant="small">
            Accions
          </Typography>
        </Grid>
      </Grid>

      {/* Llista de Conceptes Pendents de Validar */}
      {sortedMoviments.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1, opacity: 0.7 }} />
          <Typography variant="body1">
            No hi ha cap article pendent de validar. Totes les propostes estan al dia!
          </Typography>
        </Box>
      ) : (
        sortedMoviments.map((cm) => {
          const isEditing = editingId === cm.id;
          const isLoadingThis = loadingActionId === cm.id;
          const resolvedPath = resolveMovementConceptAndPath(cm, enrichedTree, conceptesNodesMap);
          const colorImport = getColorForAmount(cm.importe, false);
          const movimentText = cm.moviment?.moviment || cm.moviment || '';
          const tipus = cm.moviment?.tipus || '';

          return (
            <Box
              key={cm.id}
              sx={{
                borderBottom: '1px solid #f0f0f0',
                bgcolor: isEditing ? '#fffde7' : '#ffffff',
                transition: 'background-color 0.2s',
                '&:hover': {
                  bgcolor: isEditing ? '#fffde7' : '#f8fafc'
                }
              }}
            >
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{
                  padding: '8px 10px'
                }}
              >
                {/* Data */}
                <Grid size={{ xs: 12, sm: 1 }}>
                  <Typography variant="small" sx={{ fontSize: '0.80rem' }}>
                    {showYear ? strDateToDDMMYYYY(cm.data) : strDateToShort(cm.data)}
                  </Typography>
                </Grid>

                {/* Tipus */}
                <Grid size={{ xs: 12, sm: 0.5 }} sx={{ display: 'flex', alignItems: 'center' }}>
                  <TipusMovIcon tipus={tipus} />
                </Grid>

                {/* Concepte Proposat / Selector */}
                <Grid size={{ xs: 12, sm: 2.5 }} sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  {isEditing ? (
                    <SelectConcepteTextField
                      size="small"
                      value={
                        editingData?.conceptePath ||
                        (typeof editingData?.nodeFulla === 'object' ? editingData?.nodeFulla?.conceptePath : null) ||
                        editingData?.concepte ||
                        ''
                      }
                      onChange={onChangeConcepte}
                      listConceptes={targetConceptList}
                    />
                  ) : (
                    <ConcepteMovementDisplay
                      movement={cm}
                      pathInfo={resolvedPath}
                      enrichedTree={enrichedTree}
                      maxWidth="200px"
                    />
                  )}
                </Grid>

                {/* Moviment pare */}
                <Grid size={{ xs: 12, sm: 3.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#334155' }}>
                    {movimentText}
                  </Typography>
                </Grid>

                {/* Descripció / Article Amazon */}
                <Grid size={{ xs: 12, sm: 2 }}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={editingData?.descripcio || ''}
                      onChange={(e) => setEditingData(prev => ({ ...prev, descripcio: e.target.value }))}
                      placeholder="Descripció de l'article"
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem', py: 0.6 } }}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      title={cm.descripcio || ''}
                      sx={{
                        fontSize: '0.78rem',
                        color: '#64748b',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {cm.descripcio || 'Sense descripció'}
                    </Typography>
                  )}
                </Grid>

                {/* Import */}
                <Grid size={{ xs: 12, sm: 1.2 }} container direction="column" alignItems="flex-end">
                  <Typography variant="body2" fontWeight="bold" color={colorImport}>
                    {formatEuro(cm.importe)}
                  </Typography>
                </Grid>

                {/* Accions per fila */}
                <Grid size={{ xs: 12, sm: 1.3 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  {isLoadingThis ? (
                    <CircularProgress size={20} sx={{ m: 1 }} />
                  ) : isEditing ? (
                    <>
                      <Tooltip title="Guardar canvis i validar com a OK">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveAndValidate(cm.id)}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel·lar edició">
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={handleCancelEdit}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Aprovar / Validar proposta (passa a OK)">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleValidateSingle(cm.id)}
                          sx={{
                            '&:hover': { bgcolor: '#e8f5e9' }
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modificar concepte o descripció abans de validar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleStartEdit(cm)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          );
        })
      )}

      {/* Diàleg de Confirmació per Validar Tots */}
      <Dialog
        open={confirmValidateAllOpen}
        onClose={() => setConfirmValidateAllOpen(false)}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirmar Aprovació Massiva
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voleu aprovar i marcar com a <strong>OK</strong> totes les propostes de conciliació ({totalMoviments} articles)?
            Tots aquests articles quedaran consolidats i validats al sistema.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmValidateAllOpen(false)} color="inherit" disabled={isValidatingAll}>
            Cancel·lar
          </Button>
          <Button
            onClick={handleValidateAll}
            color="success"
            variant="contained"
            disabled={isValidatingAll}
            startIcon={isValidatingAll ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
          >
            {isValidatingAll ? 'Validant...' : 'Validar Tots'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MovimentsPendentsValidarList;
